import { Link } from "wouter";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Nav */}
      <nav className="border-b border-slate-800 bg-slate-950/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/">
            <span className="text-white font-bold tracking-widest text-sm cursor-pointer hover:text-slate-300 transition-colors">
              VEXIS
            </span>
          </Link>
          <Link href="/">
            <span className="text-xs text-slate-400 hover:text-slate-200 transition-colors cursor-pointer">
              ← Back to site
            </span>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="border-b border-slate-800 bg-slate-900/40">
        <div className="container mx-auto px-6 py-12 md:py-16 max-w-3xl">
          <p className="text-xs font-semibold tracking-[0.2em] text-slate-500 uppercase mb-3">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-slate-400 text-sm">
            Vexis Talent Agency &mdash; a trading name of LHA Holdings Ltd, Auckland, New Zealand
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 md:py-16 max-w-3xl">
        <div className="space-y-12">

          {/* 1 */}
          <section>
            <SectionLabel number="01" />
            <h2 className="text-lg font-semibold text-white mb-4">Who We Are</h2>
            <div className="space-y-3 text-slate-400 leading-relaxed">
              <p>
                This privacy policy covers Vexis Talent Agency, a trading name of{" "}
                <strong className="text-slate-200">LHA Holdings Ltd</strong>, incorporated in Auckland,
                New Zealand.
              </p>
              <p>
                We operate as an external hiring operations and candidate assessment platform.
                We are not a recruitment agency.
              </p>
              <p>
                Privacy enquiries and requests should be directed to:{" "}
                <a
                  href="mailto:compliance@vexistalentagency.com"
                  className="text-white underline underline-offset-2 hover:text-slate-300 transition-colors"
                >
                  compliance@vexistalentagency.com
                </a>
              </p>
            </div>
          </section>

          <Divider />

          {/* 2 */}
          <section>
            <SectionLabel number="02" />
            <h2 className="text-lg font-semibold text-white mb-4">What We Collect</h2>
            <p className="text-slate-400 leading-relaxed mb-4">
              When you complete a Vexis candidate assessment, we collect the following and nothing more:
            </p>
            <ul className="space-y-2">
              {[
                "Your name and email address",
                "The job title you applied for",
                "Your video interview responses (audio and video)",
                "A text transcript of your responses",
                "Your assessment scores",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-300 text-sm">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-slate-500 text-sm mt-5 leading-relaxed">
              We do <strong className="text-slate-400">not</strong> collect your resume, identity
              documents, or any financial information.
            </p>
          </section>

          <Divider />

          {/* 3 */}
          <section>
            <SectionLabel number="03" />
            <h2 className="text-lg font-semibold text-white mb-4">Why We Collect It</h2>
            <p className="text-slate-400 leading-relaxed mb-4">
              We collect your information solely to provide candidate assessment services to the hiring
              organisation that invited you to complete an assessment.
            </p>
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-3 text-sm">
              <div className="flex gap-3">
                <span className="text-slate-500 font-medium w-28 flex-shrink-0">Legal basis</span>
                <span className="text-slate-300">
                  Legitimate interest — assessing your suitability for a role you applied for.
                </span>
              </div>
              <div className="flex gap-3">
                <span className="text-slate-500 font-medium w-28 flex-shrink-0">Consent</span>
                <span className="text-slate-300">
                  Given explicitly at the start of your interview session.
                </span>
              </div>
            </div>
          </section>

          <Divider />

          {/* 4 */}
          <section>
            <SectionLabel number="04" />
            <h2 className="text-lg font-semibold text-white mb-4">How Long We Keep It</h2>
            <div className="space-y-4">
              <RetentionRow
                label="Video recordings"
                detail="Deleted within 24 hours of your interview being reviewed."
              />
              <RetentionRow
                label="Transcripts & scores"
                detail="Retained for 24 months from your assessment date, then permanently deleted."
              />
              <RetentionRow
                label="Deletion requests"
                detail="Actioned within 30 days of receipt."
              />
            </div>
          </section>

          <Divider />

          {/* 5 */}
          <section>
            <SectionLabel number="05" />
            <h2 className="text-lg font-semibold text-white mb-4">Who We Share It With</h2>
            <p className="text-slate-400 leading-relaxed mb-5">
              Your assessment results are shared only with the hiring organisation that invited you,
              delivered via their applicant tracking system.{" "}
              <strong className="text-slate-200">
                We do not sell your data or share it with any other third parties.
              </strong>
            </p>
            <p className="text-sm text-slate-500 mb-3 font-medium uppercase tracking-wider">
              Technical sub-processors
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { name: "Supabase", role: "Database", location: "Singapore" },
                { name: "Cloudflare", role: "Video delivery", location: "Global CDN" },
                { name: "Replit", role: "Application hosting", location: "United States" },
              ].map((p) => (
                <div key={p.name} className="bg-slate-900 border border-slate-800 rounded-lg p-4">
                  <p className="text-white font-medium text-sm">{p.name}</p>
                  <p className="text-slate-400 text-xs mt-1">{p.role}</p>
                  <p className="text-slate-600 text-xs">{p.location}</p>
                </div>
              ))}
            </div>
          </section>

          <Divider />

          {/* 6 */}
          <section>
            <SectionLabel number="06" />
            <h2 className="text-lg font-semibold text-white mb-4">Your Rights</h2>
            <p className="text-slate-400 leading-relaxed mb-5">
              Under the NZ Privacy Act 2020, you have the right to:
            </p>
            <ul className="space-y-2 mb-6">
              {[
                "Access the personal information we hold about you",
                "Correct any inaccuracies in your data",
                "Request deletion of your data",
                "Withdraw your consent at any time",
              ].map((right) => (
                <li key={right} className="flex items-start gap-3 text-slate-300 text-sm">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                  {right}
                </li>
              ))}
            </ul>
            <p className="text-slate-400 text-sm">
              To exercise any of these rights, email:{" "}
              <a
                href="mailto:compliance@vexistalentagency.com"
                className="text-white underline underline-offset-2 hover:text-slate-300 transition-colors"
              >
                compliance@vexistalentagency.com
              </a>
            </p>
          </section>

          <Divider />

          {/* 7 */}
          <section>
            <SectionLabel number="07" />
            <h2 className="text-lg font-semibold text-white mb-4">Cookies</h2>
            <p className="text-slate-400 leading-relaxed">
              We use essential cookies only — those necessary for the platform to function. We do not
              use advertising cookies, tracking pixels, or any third-party analytics that profile you
              across other websites.
            </p>
          </section>

          <Divider />

          {/* 8 */}
          <section>
            <SectionLabel number="08" />
            <h2 className="text-lg font-semibold text-white mb-4">Last Updated</h2>
            <p className="text-slate-400 leading-relaxed">
              This policy was last updated in{" "}
              <strong className="text-slate-200">April 2026</strong>. Material changes will be
              communicated to active candidates via email.
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 mt-16 py-10">
        <div className="container mx-auto px-6 max-w-3xl flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600">
          <p>© 2026 Vexis Talent Agency &mdash; LHA Holdings Ltd</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-slate-400 transition-colors">Terms & Conditions</Link>
            <Link href="/" className="hover:text-slate-400 transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SectionLabel({ number }: { number: string }) {
  return (
    <p className="text-xs font-semibold tracking-[0.2em] text-slate-600 uppercase mb-2">
      Section {number}
    </p>
  );
}

function Divider() {
  return <div className="border-t border-slate-800" />;
}

function RetentionRow({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-3 border-b border-slate-800 last:border-0">
      <span className="text-slate-400 font-medium text-sm sm:w-44 flex-shrink-0">{label}</span>
      <span className="text-slate-300 text-sm leading-relaxed">{detail}</span>
    </div>
  );
}
